import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { socketService } from "../../../services/socket";

// =============================================================================
// TIPOS
// =============================================================================

interface WordItem {
  text: string;
  value: number;
  category: string;
}

interface ParticipantItem {
  id: string;
  name: string;
  alias: string;
  image: string;
  number: number;
  mentions: number;
  stories: number;
  trending: number;
  score: number;
}

interface TagItem {
  tagId: string;
  tagName: string;
  tagColor: string;
  category: string;
  count: number;
}

interface VelocityPoint {
  hour: string;
  label: string;
  date: string;
  logCount: number;
  storyCount: number;
}

interface TensionPoint {
  bucket: string;
  ts: number;
  label: string;
  avgRating: number;
  logCount: number;
  ratedCount: number;
}

interface EmotionBucket {
  ts: number;
  label: string;
}

interface EmotionCategory {
  id: string;
  name: string;
  color: string;
  total: number;
}

interface EmotionTimeline {
  buckets: EmotionBucket[];
  categories: EmotionCategory[];
  matrix: number[][];
  maxCount: number;
}

interface CameraPerformanceItem {
  cameraId: string;
  name: string;
  count: number;
  avgRating: number;
  highlightCount: number;
  ratedCount: number;
}

interface ActivityParticipant {
  id: string;
  name: string;
  alias: string;
  image: string;
  total: number;
}

interface ParticipantActivity {
  buckets: EmotionBucket[];
  participants: ActivityParticipant[];
  matrix: number[][];
  maxCount: number;
}

interface PulseData {
  wordCloud: WordItem[];
  participantRadar: ParticipantItem[];
  tagDistribution: TagItem[];
  storyVelocity: VelocityPoint[];
  tensionCurve: TensionPoint[];
  emotionTimeline: EmotionTimeline;
  cameraPerformance: CameraPerformanceItem[];
  participantActivity: ParticipantActivity;
  totals: {
    logs: number;
    stories: number;
    participants: number;
    tags: number;
    activeHours: number;
  };
  cached: boolean;
  lastUpdated: number;
}

// =============================================================================
// CONSTANTES Y COLORES
// =============================================================================

const GOLD = "#F59E0B";
const GOLD_LIGHT = "rgba(245, 158, 11, 0.15)";
const GOLD_BORDER = "rgba(245, 158, 11, 0.3)";
const GOLD_DIM = "rgba(245, 158, 11, 0.6)";
const BG_CARD = "#1a1a1a";
const BG_CARD_HOVER = "#222222";

// Word cloud color palette (shades of gold)
const WORD_COLORS = [
  "#F59E0B",
  "#FCD34D",
  "#FBBF24",
  "#F97316",
  "#EAB308",
  "#D97706",
  "#B45309",
  "#FDE68A",
  "#FEF3C7",
  "#FFFBEB",
];

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

interface PulseDashboardProps {
  projectId?: string | null;
}

export const PulseDashboard: React.FC<PulseDashboardProps> = ({ projectId }) => {
  const { t } = useTranslation();
  const [pulseData, setPulseData] = useState<PulseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeWidget, setActiveWidget] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<number>(6); // Default 6 hours
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cargar datos PULSE
  const loadPulseData = useCallback(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    let settled = false;
    const timer = setTimeout(() => {
      if (!settled) {
        settled = true;
        setError("Timeout waiting for PULSE data");
        setLoading(false);
      }
    }, 10000);
    socketService.emit(
      "get-pulse-data",
      { windowHours: timeRange },
      (data: PulseData | { error: string }) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        if ("error" in data) {
          setError(data.error);
        } else {
          setPulseData(data);
          setError(null);
        }
        setLoading(false);
      },
    );
  }, [projectId, timeRange]);

  useEffect(() => {
    loadPulseData();
    // Auto-refresh cada 60s
    refreshTimerRef.current = setInterval(loadPulseData, 60000);
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [loadPulseData]);

  // Si no hay proyecto seleccionado, mostrar estado vacío amigable
  if (!projectId) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          color: GOLD_DIM,
          gap: "16px",
          flexDirection: "column",
        }}
      >
        <i
          className="bx bx-folder-open"
          style={{ fontSize: "3rem", color: GOLD, opacity: 0.4 }}
        ></i>
        <span style={{ fontWeight: 700, letterSpacing: "2px", fontSize: "0.9rem", color: GOLD }}>
          {t("showrunner.pulse.selectProject")}
        </span>
        <span
          style={{
            fontSize: "0.75rem",
            color: "rgba(255,255,255,0.35)",
            maxWidth: "300px",
            textAlign: "center",
          }}
        >
          {t("showrunner.pulse.clickProjects")}
        </span>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          color: GOLD_DIM,
          gap: "12px",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: `3px solid ${GOLD_BORDER}`,
            borderTop: `3px solid ${GOLD}`,
            borderRadius: "50%",
            animation: "spin 1s linear infinite",
          }}
        />
        <span style={{ fontWeight: 700, letterSpacing: "2px", fontSize: "0.8rem" }}>
          {t("showrunner.pulse.loading")}
        </span>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          minHeight: "400px",
          color: "#ff4444",
          gap: "8px",
        }}
      >
        <i className="bx bx-error-circle" style={{ fontSize: "1.5rem" }}></i>
        <span>{error}</span>
        <button
          onClick={loadPulseData}
          style={{
            background: GOLD,
            border: "none",
            color: "#000",
            padding: "6px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: 700,
            marginLeft: "12px",
          }}
        >
          {t("showrunner.pulse.retry")}
        </button>
      </div>
    );
  }

  if (!pulseData) return null;

  return (
    <div
      style={{
        height: "100%",
        overflow: "auto",
        padding: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
      className="thin-scrollbar"
    >
      {/* Controles: Time Range Selector */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
        }}
      >
        <label
          style={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "rgba(255,255,255,0.5)",
            letterSpacing: "1px",
          }}
        >
          {t("showrunner.pulse.timeRange", "RANGO DE TIEMPO:")}
        </label>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(Number(e.target.value))}
          style={{
            background: "#1a1a1a",
            color: "#F59E0B",
            border: "1px solid rgba(245, 158, 11, 0.3)",
            padding: "4px 12px",
            borderRadius: "6px",
            fontSize: "0.8rem",
            fontWeight: 800,
            outline: "none",
            cursor: "pointer",
          }}
        >
          <option value={1}>Última hora</option>
          <option value={6}>Últimas 6 horas</option>
          <option value={12}>Últimas 12 horas</option>
          <option value={24}>Últimas 24 horas</option>
          <option value={72}>Últimos 3 días</option>
          <option value={999999}>Todo el proyecto</option>
        </select>
      </div>

      {/* Header stats */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "12px",
        }}
      >
        <StatCard icon="bx-file" label={t("showrunner.pulse.logs")} value={pulseData.totals.logs} />
        <StatCard
          icon="bx-book-open"
          label={t("showrunner.pulse.stories")}
          value={pulseData.totals.stories}
        />
        <StatCard
          icon="bx-user"
          label={t("showrunner.pulse.participants")}
          value={pulseData.totals.participants}
        />
        <StatCard
          icon="bx-purchase-tag"
          label={t("showrunner.pulse.tags")}
          value={pulseData.totals.tags}
        />
      </div>

      {/* Main grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "16px",
          flex: 1,
        }}
      >
        {/* Word Cloud */}
        <WidgetCard
          title={t("showrunner.pulse.wordCloud")}
          icon="bx-cloud"
          description={t(
            "showrunner.pulse.desc.wordCloud",
            "Palabras o conceptos más frecuentes extraídos de los registros (logs).",
          )}
          expanded={activeWidget === "wordcloud"}
          onToggle={() => setActiveWidget(activeWidget === "wordcloud" ? null : "wordcloud")}
        >
          <WordCloudWidget words={pulseData.wordCloud} />
        </WidgetCard>

        {/* Participant Radar */}
        <WidgetCard
          title={t("showrunner.pulse.participantRadar")}
          icon="bx-user-circle"
          description={t(
            "showrunner.pulse.desc.participantRadar",
            "Clasificación de participantes según menciones, historias y tendencia global.",
          )}
          expanded={activeWidget === "participants"}
          onToggle={() => setActiveWidget(activeWidget === "participants" ? null : "participants")}
        >
          <ParticipantRadarWidget participants={pulseData.participantRadar} />
        </WidgetCard>

        {/* Tag Distribution */}
        <WidgetCard
          title={t("showrunner.pulse.tagDistribution")}
          icon="bx-purchase-tag"
          description={t(
            "showrunner.pulse.desc.tagDistribution",
            "Desglose visual del uso de etiquetas, agrupadas por categorías.",
          )}
          expanded={activeWidget === "tags"}
          onToggle={() => setActiveWidget(activeWidget === "tags" ? null : "tags")}
        >
          <TagDistributionWidget tags={pulseData.tagDistribution} />
        </WidgetCard>

        {/* Story Velocity */}
        <WidgetCard
          title={t("showrunner.pulse.storyVelocity")}
          icon="bx-line-chart"
          description={t(
            "showrunner.pulse.desc.storyVelocity",
            "Ritmo de actividad mostrando la cantidad de registros generados por hora.",
          )}
          expanded={activeWidget === "velocity"}
          onToggle={() => setActiveWidget(activeWidget === "velocity" ? null : "velocity")}
        >
          <StoryVelocityWidget velocity={pulseData.storyVelocity} />
        </WidgetCard>

        {/* Tension Curve */}
        <WidgetCard
          title={t("showrunner.pulse.tensionCurve")}
          icon="bx-trending-up"
          description={t(
            "showrunner.pulse.desc.tensionCurve",
            "Curva de temperatura basada en las calificaciones de los eventos a lo largo del tiempo.",
          )}
          expanded={activeWidget === "tension"}
          onToggle={() => setActiveWidget(activeWidget === "tension" ? null : "tension")}
        >
          <TensionCurveWidget tension={pulseData.tensionCurve} />
        </WidgetCard>

        {/* Emotion Timeline */}
        <WidgetCard
          title={t("showrunner.pulse.emotionTimeline")}
          icon="bx-grid-alt"
          description={t(
            "showrunner.pulse.desc.emotionTimeline",
            "Línea de tiempo cruzando estados emocionales detectados con los momentos del proyecto.",
          )}
          expanded={activeWidget === "emotions"}
          onToggle={() => setActiveWidget(activeWidget === "emotions" ? null : "emotions")}
        >
          <EmotionTimelineWidget timeline={pulseData.emotionTimeline} />
        </WidgetCard>

        {/* Camera Performance */}
        <WidgetCard
          title={t("showrunner.pulse.cameraPerformance")}
          icon="bx-camera"
          description={t(
            "showrunner.pulse.desc.cameraPerformance",
            "Métricas de cada cámara: eventos totales, calificaciones promedio y momentos destacados.",
          )}
          expanded={activeWidget === "cameras"}
          onToggle={() => setActiveWidget(activeWidget === "cameras" ? null : "cameras")}
        >
          <CameraPerformanceWidget cameras={pulseData.cameraPerformance} />
        </WidgetCard>

        {/* Participant Activity Timeline */}
        <WidgetCard
          title={t("showrunner.pulse.participantActivity")}
          icon="bx-time-five"
          description={t(
            "showrunner.pulse.desc.participantActivity",
            "Mapa de calor interactivo que rastrea en qué momentos cada participante estuvo más activo.",
          )}
          expanded={activeWidget === "activity"}
          onToggle={() => setActiveWidget(activeWidget === "activity" ? null : "activity")}
        >
          <ParticipantActivityWidget activity={pulseData.participantActivity} />
        </WidgetCard>
      </div>

      {/* Footer: last updated */}
      <div
        style={{
          textAlign: "center",
          fontSize: "0.65rem",
          color: "rgba(255,255,255,0.3)",
          padding: "4px 0",
          letterSpacing: "1px",
        }}
      >
        {pulseData.cached ? t("showrunner.pulse.cached") : t("showrunner.pulse.live")} ·
        {t("showrunner.pulse.updated")} {new Date(pulseData.lastUpdated).toLocaleTimeString()} ·
        {t("showrunner.pulse.autoRefresh")}
        <button
          onClick={loadPulseData}
          style={{
            background: "transparent",
            border: `1px solid ${GOLD_BORDER}`,
            color: GOLD_DIM,
            padding: "2px 10px",
            borderRadius: "4px",
            cursor: "pointer",
            marginLeft: "12px",
            fontSize: "0.6rem",
            fontWeight: 700,
            letterSpacing: "1px",
          }}
        >
          {t("showrunner.pulse.refresh")}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// STAT CARD (Top bar)
// =============================================================================

const StatCard: React.FC<{ icon: string; label: string; value: number }> = ({
  icon,
  label,
  value,
}) => (
  <div
    style={{
      background: BG_CARD,
      borderRadius: "10px",
      padding: "14px 16px",
      border: `1px solid ${GOLD_BORDER}`,
      display: "flex",
      alignItems: "center",
      gap: "12px",
      transition: "all 0.2s",
    }}
  >
    <i className={`bx ${icon}`} style={{ fontSize: "1.5rem", color: GOLD }}></i>
    <div>
      <div style={{ fontSize: "1.3rem", fontWeight: 900, color: "#fff" }}>
        {value.toLocaleString()}
      </div>
      <div style={{ fontSize: "0.6rem", color: GOLD_DIM, fontWeight: 700, letterSpacing: "2px" }}>
        {label}
      </div>
    </div>
  </div>
);

// =============================================================================
// WIDGET CARD (Container)
// =============================================================================

const WidgetCard: React.FC<{
  title: string;
  icon: string;
  description?: string;
  children: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
}> = ({ title, icon, description, children, expanded, onToggle }) => (
  <div
    style={{
      background: BG_CARD,
      borderRadius: "12px",
      border: `1px solid ${expanded ? GOLD : GOLD_BORDER}`,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      transition: "all 0.3s",
      gridColumn: expanded ? "1 / -1" : "auto",
      minHeight: expanded ? "400px" : "280px",
      maxHeight: expanded ? "600px" : "350px",
    }}
  >
    {/* Widget header */}
    <div
      onClick={onToggle}
      style={{
        padding: "12px 16px",
        background: GOLD_LIGHT,
        borderBottom: `1px solid ${GOLD_BORDER}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <i className={`bx ${icon}`} style={{ color: GOLD, fontSize: "1.1rem" }}></i>
        <span style={{ fontWeight: 800, fontSize: "0.75rem", letterSpacing: "2px", color: GOLD }}>
          {title}
        </span>
        {description && (
          <i
            className="bx bx-info-circle"
            title={description}
            style={{
              color: "rgba(245, 158, 11, 0.6)",
              fontSize: "0.85rem",
              marginLeft: "4px",
              cursor: "help",
            }}
          ></i>
        )}
      </div>
      <i
        className={`bx bx-${expanded ? "collapse" : "expand"}`}
        style={{ color: GOLD_DIM, fontSize: "1rem" }}
      ></i>
    </div>

    {/* Widget body */}
    <div
      style={{
        flex: 1,
        overflow: "auto",
        padding: "12px",
      }}
      className="thin-scrollbar"
    >
      {children}
    </div>
  </div>
);

// =============================================================================
// WORD CLOUD WIDGET
// =============================================================================

const WordCloudWidget: React.FC<{ words: WordItem[] }> = ({ words }) => {
  const { t } = useTranslation();
  if (!words || words.length === 0) {
    return <EmptyState icon="bx-cloud" text={t("showrunner.pulse.noTextData")} />;
  }

  const maxValue = Math.max(...words.map((w) => w.value));

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        alignItems: "center",
        gap: "6px",
        padding: "8px",
        minHeight: "200px",
      }}
    >
      {words.slice(0, 60).map((word, idx) => {
        const ratio = word.value / maxValue;
        const fontSize = 0.6 + ratio * 1.8; // 0.6rem to 2.4rem
        const opacity = 0.4 + ratio * 0.6;
        const color = WORD_COLORS[idx % WORD_COLORS.length];

        return (
          <span
            key={word.text}
            title={`"${word.text}" — ${word.value} menciones`}
            style={{
              fontSize: `${fontSize}rem`,
              fontWeight: ratio > 0.5 ? 900 : ratio > 0.3 ? 700 : 500,
              color,
              opacity,
              cursor: "default",
              transition: "all 0.2s",
              padding: "2px 4px",
              lineHeight: 1.2,
              letterSpacing: ratio > 0.5 ? "1px" : "0",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.2)";
              e.currentTarget.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.opacity = String(opacity);
            }}
          >
            {word.text}
          </span>
        );
      })}
    </div>
  );
};

// =============================================================================
// PARTICIPANT RADAR WIDGET
// =============================================================================

const ParticipantRadarWidget: React.FC<{ participants: ParticipantItem[] }> = ({
  participants,
}) => {
  const { t } = useTranslation();
  if (!participants || participants.length === 0) {
    return <EmptyState icon="bx-user" text={t("showrunner.pulse.noParticipantData")} />;
  }

  const maxScore = Math.max(...participants.map((p) => p.score));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {participants.slice(0, 15).map((p, idx) => {
        const barWidth = maxScore > 0 ? (p.score / maxScore) * 100 : 0;
        const isLowCoverage = p.mentions < 5;
        const isTrending = p.trending > 1.5;

        return (
          <div
            key={p.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "6px 8px",
              borderRadius: "8px",
              background: idx < 3 ? GOLD_LIGHT : "transparent",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = BG_CARD_HOVER)}
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = idx < 3 ? GOLD_LIGHT : "transparent")
            }
          >
            {/* Rank */}
            <span
              style={{
                width: "22px",
                fontWeight: 900,
                fontSize: "0.75rem",
                color: idx < 3 ? GOLD : "rgba(255,255,255,0.4)",
                textAlign: "center",
              }}
            >
              {idx + 1}
            </span>

            {/* Avatar */}
            <div
              style={{
                width: "28px",
                height: "28px",
                borderRadius: "50%",
                background: p.image ? `url(${p.image}) center/cover` : BG_CARD_HOVER,
                border: `2px solid ${idx < 3 ? GOLD : "rgba(255,255,255,0.1)"}`,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.7rem",
                fontWeight: 700,
                color: GOLD,
              }}
            >
              {!p.image && (p.name?.[0] || "?")}
            </div>

            {/* Name + stats */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    color: "#ddd",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {p.alias || p.name}
                </span>
                {isTrending && (
                  <span
                    style={{
                      fontSize: "0.55rem",
                      background: "rgba(245, 158, 11, 0.2)",
                      color: GOLD,
                      padding: "1px 4px",
                      borderRadius: "4px",
                      fontWeight: 800,
                    }}
                  >
                    {t("showrunner.pulse.trending")}
                  </span>
                )}
                {isLowCoverage && (
                  <span
                    style={{
                      fontSize: "0.55rem",
                      background: "rgba(239, 68, 68, 0.2)",
                      color: "#ef4444",
                      padding: "1px 4px",
                      borderRadius: "4px",
                      fontWeight: 800,
                    }}
                  >
                    {t("showrunner.pulse.low")}
                  </span>
                )}
              </div>
              {/* Progress bar */}
              <div
                style={{
                  height: "4px",
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "2px",
                  marginTop: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${barWidth}%`,
                    background: `linear-gradient(90deg, ${GOLD} 0%, #FCD34D 100%)`,
                    borderRadius: "2px",
                    transition: "width 0.5s ease",
                  }}
                />
              </div>
            </div>

            {/* Mentions count */}
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontWeight: 800, fontSize: "0.85rem", color: GOLD }}>{p.mentions}</div>
              <div
                style={{
                  fontSize: "0.5rem",
                  color: "rgba(255,255,255,0.3)",
                  letterSpacing: "0.5px",
                }}
              >
                {p.stories}st
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// =============================================================================
// TAG DISTRIBUTION WIDGET
// =============================================================================

const TagDistributionWidget: React.FC<{ tags: TagItem[] }> = ({ tags }) => {
  const { t } = useTranslation();
  if (!tags || tags.length === 0) {
    return <EmptyState icon="bx-purchase-tag" text={t("showrunner.pulse.noTagData")} />;
  }

  // Agrupar por categoría
  const byCategory: Record<string, { total: number; tags: TagItem[] }> = {};
  tags.forEach((t) => {
    if (!byCategory[t.category]) byCategory[t.category] = { total: 0, tags: [] };
    byCategory[t.category].total += t.count;
    byCategory[t.category].tags.push(t);
  });

  const categories = Object.entries(byCategory).sort(([, a], [, b]) => b.total - a.total);

  const maxTotal = Math.max(...categories.map(([, v]) => v.total));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {categories.slice(0, 10).map(([catName, catData]) => (
        <div
          key={catName}
          style={{
            background: "rgba(255,255,255,0.03)",
            borderRadius: "8px",
            padding: "8px 12px",
            border: `1px solid rgba(255,255,255,0.05)`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "6px",
            }}
          >
            <span
              style={{
                fontWeight: 800,
                fontSize: "0.7rem",
                color: GOLD,
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              {catName}
            </span>
            <span
              style={{
                fontWeight: 700,
                fontSize: "0.75rem",
                color: "rgba(255,255,255,0.7)",
              }}
            >
              {catData.total}
            </span>
          </div>
          {/* Bar */}
          <div
            style={{
              height: "6px",
              background: "rgba(255,255,255,0.05)",
              borderRadius: "3px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "100%",
                width: `${(catData.total / maxTotal) * 100}%`,
                background: `linear-gradient(90deg, ${GOLD} 0%, #FCD34D 100%)`,
                borderRadius: "3px",
                transition: "width 0.5s ease",
              }}
            />
          </div>
          {/* Tags inline */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "4px",
              marginTop: "6px",
            }}
          >
            {catData.tags.slice(0, 5).map((t) => (
              <span
                key={t.tagId}
                style={{
                  fontSize: "0.55rem",
                  padding: "2px 6px",
                  borderRadius: "4px",
                  background: `${t.tagColor}20`,
                  color: t.tagColor || GOLD,
                  border: `1px solid ${t.tagColor}40`,
                  fontWeight: 600,
                }}
              >
                {t.tagName} ({t.count})
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// =============================================================================
// STORY VELOCITY WIDGET
// =============================================================================

const StoryVelocityWidget: React.FC<{ velocity: VelocityPoint[] }> = ({ velocity }) => {
  const { t } = useTranslation();
  if (!velocity || velocity.length === 0) {
    return <EmptyState icon="bx-line-chart" text={t("showrunner.pulse.noVelocityData")} />;
  }

  const maxLogs = Math.max(...velocity.map((v) => v.logCount), 1);
  // Tomar últimas 24 entradas para que quepa
  const displayData = velocity.slice(-24);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Chart area */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "stretch",
          gap: "2px",
          padding: "8px 4px 4px 4px",
          minHeight: "120px",
        }}
      >
        {displayData.map((v, _idx) => {
          const height = (v.logCount / maxLogs) * 100;
          return (
            <div
              key={v.hour}
              title={`${v.label} (${v.date})\n${v.logCount} logs · ${v.storyCount} stories`}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "2px",
                height: "100%",
              }}
            >
              {/* Log count label */}
              {v.logCount > 0 && (
                <span
                  style={{
                    fontSize: "0.45rem",
                    color: GOLD_DIM,
                    fontWeight: 700,
                  }}
                >
                  {v.logCount}
                </span>
              )}
              {/* Bar */}
              <div
                style={{
                  width: "100%",
                  maxWidth: "24px",
                  height: `${Math.max(height, 2)}%`,
                  background: `linear-gradient(180deg, ${GOLD} 0%, rgba(245, 158, 11, 0.3) 100%)`,
                  borderRadius: "3px 3px 0 0",
                  transition: "height 0.3s ease",
                  minHeight: "2px",
                }}
              />
            </div>
          );
        })}
      </div>

      {/* X axis labels */}
      <div
        style={{
          display: "flex",
          gap: "2px",
          borderTop: `1px solid ${GOLD_BORDER}`,
          paddingTop: "4px",
        }}
      >
        {displayData.map((v, idx) => (
          <div
            key={v.hour}
            style={{
              flex: 1,
              textAlign: "center",
              fontSize: "0.45rem",
              color: "rgba(255,255,255,0.3)",
              fontFamily: "var(--font-mono, monospace)",
            }}
          >
            {idx % 3 === 0 ? v.label : ""}
          </div>
        ))}
      </div>

      {/* Summary */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          marginTop: "8px",
          padding: "8px",
          background: GOLD_LIGHT,
          borderRadius: "6px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "1rem", fontWeight: 900, color: GOLD }}>
            {Math.round(
              velocity.reduce((s, v) => s + v.logCount, 0) / Math.max(velocity.length, 1),
            )}
          </div>
          <div
            style={{ fontSize: "0.5rem", color: GOLD_DIM, letterSpacing: "1px", fontWeight: 700 }}
          >
            {t("showrunner.pulse.avgLogsHr")}
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "1rem", fontWeight: 900, color: GOLD }}>
            {velocity.length > 0 ? velocity[velocity.length - 1].logCount : 0}
          </div>
          <div
            style={{ fontSize: "0.5rem", color: GOLD_DIM, letterSpacing: "1px", fontWeight: 700 }}
          >
            {t("showrunner.pulse.lastHour")}
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "1rem", fontWeight: 900, color: GOLD }}>
            {Math.max(...velocity.map((v) => v.logCount))}
          </div>
          <div
            style={{ fontSize: "0.5rem", color: GOLD_DIM, letterSpacing: "1px", fontWeight: 700 }}
          >
            {t("showrunner.pulse.peak")}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// TENSION CURVE WIDGET (Termómetro de tensión por rating)
// =============================================================================

const TENSION_COLOR_STOPS = [
  { v: 0, color: "#3b82f6" }, // azul (frío)
  { v: 2, color: "#22c55e" }, // verde
  { v: 3, color: "#eab308" }, // amarillo
  { v: 4, color: "#f97316" }, // naranja
  { v: 5, color: "#ef4444" }, // rojo (caliente)
];

function tensionColor(rating: number): string {
  if (rating <= 0) return "rgba(255,255,255,0.15)";
  for (let i = TENSION_COLOR_STOPS.length - 1; i >= 0; i--) {
    if (rating >= TENSION_COLOR_STOPS[i].v) return TENSION_COLOR_STOPS[i].color;
  }
  return TENSION_COLOR_STOPS[0].color;
}

const TensionCurveWidget: React.FC<{ tension: TensionPoint[] }> = ({ tension }) => {
  const { t } = useTranslation();
  if (!tension || tension.length === 0) {
    return <EmptyState icon="bx-trending-up" text={t("showrunner.pulse.noTensionData")} />;
  }

  const ratedPoints = tension.filter((p) => p.ratedCount > 0);
  const overallAvg =
    ratedPoints.length > 0
      ? ratedPoints.reduce((s, p) => s + p.avgRating, 0) / ratedPoints.length
      : 0;
  const peak = tension.reduce((max, p) => (p.avgRating > max.avgRating ? p : max), tension[0]);
  const latest =
    [...tension].reverse().find((p) => p.ratedCount > 0) || tension[tension.length - 1];

  // SVG sparkline
  const W = 600;
  const H = 140;
  const PAD_X = 8;
  const PAD_Y = 12;
  const innerW = W - PAD_X * 2;
  const innerH = H - PAD_Y * 2;
  const xStep = tension.length > 1 ? innerW / (tension.length - 1) : 0;
  const yFor = (v: number) => PAD_Y + innerH - (Math.max(0, Math.min(5, v)) / 5) * innerH;

  const points = tension.map((p, i) => ({
    x: PAD_X + i * xStep,
    y: yFor(p.avgRating),
    p,
  }));

  const pathD = points
    .map((pt, i) => `${i === 0 ? "M" : "L"}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`)
    .join(" ");
  const areaD = `${pathD} L${(PAD_X + innerW).toFixed(1)},${(PAD_Y + innerH).toFixed(
    1,
  )} L${PAD_X.toFixed(1)},${(PAD_Y + innerH).toFixed(1)} Z`;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: "8px" }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: "140px" }}
      >
        <defs>
          <linearGradient id="tensionArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={GOLD} stopOpacity="0.35" />
            <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[1, 2, 3, 4].map((g) => (
          <line
            key={g}
            x1={PAD_X}
            x2={W - PAD_X}
            y1={yFor(g)}
            y2={yFor(g)}
            stroke="rgba(255,255,255,0.05)"
            strokeWidth="1"
          />
        ))}
        <path d={areaD} fill="url(#tensionArea)" />
        <path d={pathD} fill="none" stroke={GOLD} strokeWidth="2" strokeLinejoin="round" />
        {points.map((pt) => (
          <circle
            key={pt.p.bucket}
            cx={pt.x}
            cy={pt.y}
            r={pt.p.ratedCount > 0 ? 2.5 : 1.5}
            fill={tensionColor(pt.p.avgRating)}
            stroke="#000"
            strokeWidth="0.5"
          >
            <title>
              {pt.p.label} · {pt.p.avgRating.toFixed(2)}★ · {pt.p.logCount} logs ({pt.p.ratedCount}{" "}
              rated)
            </title>
          </circle>
        ))}
      </svg>

      {/* X axis labels (cada N) */}
      <div
        style={{
          display: "flex",
          borderTop: `1px solid ${GOLD_BORDER}`,
          paddingTop: "4px",
          fontSize: "0.5rem",
          color: "rgba(255,255,255,0.3)",
          fontFamily: "var(--font-mono, monospace)",
        }}
      >
        {tension.map((p, idx) => {
          const everyN = Math.max(1, Math.floor(tension.length / 8));
          return (
            <div key={p.bucket} style={{ flex: 1, textAlign: "center" }}>
              {idx % everyN === 0 ? p.label : ""}
            </div>
          );
        })}
      </div>

      {/* Summary footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          padding: "8px",
          background: GOLD_LIGHT,
          borderRadius: "6px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "1rem",
              fontWeight: 900,
              color: tensionColor(overallAvg),
            }}
          >
            {overallAvg.toFixed(2)}★
          </div>
          <div
            style={{ fontSize: "0.5rem", color: GOLD_DIM, letterSpacing: "1px", fontWeight: 700 }}
          >
            {t("showrunner.pulse.avgTension")}
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "1rem",
              fontWeight: 900,
              color: tensionColor(latest.avgRating),
            }}
          >
            {latest.avgRating.toFixed(2)}★
          </div>
          <div
            style={{ fontSize: "0.5rem", color: GOLD_DIM, letterSpacing: "1px", fontWeight: 700 }}
          >
            {t("showrunner.pulse.nowTension")}
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              fontSize: "1rem",
              fontWeight: 900,
              color: tensionColor(peak.avgRating),
            }}
          >
            {peak.avgRating.toFixed(2)}★
          </div>
          <div
            style={{ fontSize: "0.5rem", color: GOLD_DIM, letterSpacing: "1px", fontWeight: 700 }}
          >
            {t("showrunner.pulse.peakTension")} · {peak.label}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// EMOTION TIMELINE WIDGET (Heatmap categoría × bucket)
// =============================================================================

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return [245, 158, 11];
  return [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)];
}

const EmotionTimelineWidget: React.FC<{ timeline: EmotionTimeline }> = ({ timeline }) => {
  const { t } = useTranslation();
  if (!timeline || !timeline.categories || timeline.categories.length === 0) {
    return <EmptyState icon="bx-grid-alt" text={t("showrunner.pulse.noEmotionData")} />;
  }

  const { buckets, categories, matrix, maxCount } = timeline;
  const everyN = Math.max(1, Math.floor(buckets.length / 8));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", height: "100%" }}>
      <div style={{ overflowX: "auto" }} className="thin-scrollbar">
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: "1px",
            width: "100%",
            tableLayout: "fixed",
          }}
        >
          <tbody>
            {categories.map((cat, rowIdx) => {
              const [r, g, b] = hexToRgb(cat.color);
              return (
                <tr key={cat.id}>
                  <td
                    style={{
                      padding: "0 8px 0 0",
                      fontSize: "0.6rem",
                      fontWeight: 800,
                      color: cat.color,
                      letterSpacing: "1px",
                      whiteSpace: "nowrap",
                      textAlign: "right",
                      width: "100px",
                      maxWidth: "100px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={cat.name}
                  >
                    {cat.name}
                  </td>
                  {matrix[rowIdx].map((count, colIdx) => {
                    const intensity = maxCount > 0 ? count / maxCount : 0;
                    const alpha = count === 0 ? 0.04 : 0.15 + intensity * 0.85;
                    return (
                      <td
                        key={colIdx}
                        title={`${cat.name} · ${buckets[colIdx].label} · ${count} log${count === 1 ? "" : "s"}`}
                        style={{
                          height: "16px",
                          background:
                            count === 0
                              ? "rgba(255,255,255,0.03)"
                              : `rgba(${r}, ${g}, ${b}, ${alpha})`,
                          borderRadius: "2px",
                        }}
                      />
                    );
                  })}
                </tr>
              );
            })}
            {/* Eje X */}
            <tr>
              <td />
              {buckets.map((b, idx) => (
                <td
                  key={b.ts}
                  style={{
                    fontSize: "0.45rem",
                    color: "rgba(255,255,255,0.3)",
                    textAlign: "center",
                    fontFamily: "var(--font-mono, monospace)",
                    paddingTop: "4px",
                  }}
                >
                  {idx % everyN === 0 ? b.label : ""}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Leyenda: top categorías */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          padding: "8px",
          background: GOLD_LIGHT,
          borderRadius: "6px",
        }}
      >
        {categories.slice(0, 6).map((cat) => (
          <span
            key={cat.id}
            style={{
              fontSize: "0.6rem",
              fontWeight: 700,
              padding: "3px 8px",
              borderRadius: "4px",
              background: `${cat.color}22`,
              color: cat.color,
              border: `1px solid ${cat.color}44`,
              letterSpacing: "0.5px",
            }}
          >
            {cat.name} · {cat.total}
          </span>
        ))}
      </div>
    </div>
  );
};

// =============================================================================
// CAMERA PERFORMANCE WIDGET (Donut + barras)
// =============================================================================

const CameraPerformanceWidget: React.FC<{ cameras: CameraPerformanceItem[] }> = ({ cameras }) => {
  const { t } = useTranslation();
  if (!cameras || cameras.length === 0) {
    return <EmptyState icon="bx-camera" text={t("showrunner.pulse.noCameraData")} />;
  }

  const totalCount = cameras.reduce((s, c) => s + c.count, 0);
  const maxCount = Math.max(...cameras.map((c) => c.count), 1);

  // Donut SVG
  const SIZE = 120;
  const R = 50;
  const STROKE = 16;
  const C = 2 * Math.PI * R;
  let acc = 0;
  const slices = cameras.slice(0, 8).map((c, idx) => {
    const frac = totalCount > 0 ? c.count / totalCount : 0;
    const dash = frac * C;
    const offset = -acc * C;
    acc += frac;
    return {
      cam: c,
      dash,
      gap: C - dash,
      offset,
      color: WORD_COLORS[idx % WORD_COLORS.length],
    };
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px", height: "100%" }}>
      {/* Donut + total */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "16px",
        }}
      >
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={R}
            fill="none"
            stroke="rgba(255,255,255,0.05)"
            strokeWidth={STROKE}
          />
          {slices.map((s) => (
            <circle
              key={s.cam.cameraId}
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              stroke={s.color}
              strokeWidth={STROKE}
              strokeDasharray={`${s.dash} ${s.gap}`}
              strokeDashoffset={s.offset}
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            >
              <title>
                {s.cam.name} · {s.cam.count} ({Math.round((s.cam.count / totalCount) * 100)}%)
              </title>
            </circle>
          ))}
          <text
            x={SIZE / 2}
            y={SIZE / 2 - 4}
            textAnchor="middle"
            fill={GOLD}
            fontSize="20"
            fontWeight="900"
          >
            {totalCount}
          </text>
          <text
            x={SIZE / 2}
            y={SIZE / 2 + 14}
            textAnchor="middle"
            fill={GOLD_DIM}
            fontSize="8"
            fontWeight="700"
            letterSpacing="1"
          >
            {t("showrunner.pulse.logs")}
          </text>
        </svg>
      </div>

      {/* Barras por cámara */}
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {cameras.slice(0, 12).map((c, idx) => {
          const barWidth = (c.count / maxCount) * 100;
          const color = WORD_COLORS[idx % WORD_COLORS.length];
          return (
            <div
              key={c.cameraId}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "4px 6px",
                borderRadius: "6px",
                background: idx === 0 ? GOLD_LIGHT : "transparent",
              }}
            >
              <span
                style={{
                  width: "60px",
                  fontWeight: 800,
                  fontSize: "0.7rem",
                  color: "#ddd",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={c.name}
              >
                {c.name}
              </span>
              <div
                style={{
                  flex: 1,
                  height: "6px",
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${barWidth}%`,
                    background: color,
                    borderRadius: "3px",
                    transition: "width 0.5s ease",
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: "0.7rem",
                  fontWeight: 800,
                  color,
                  width: "30px",
                  textAlign: "right",
                }}
              >
                {c.count}
              </span>
              {c.ratedCount > 0 && (
                <span
                  style={{
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    color: GOLD,
                    width: "40px",
                    textAlign: "right",
                  }}
                  title={t("showrunner.pulse.avgRatingTooltip")}
                >
                  {c.avgRating.toFixed(1)}★
                </span>
              )}
              {c.highlightCount > 0 && (
                <span
                  style={{
                    fontSize: "0.55rem",
                    fontWeight: 800,
                    color: "#fff",
                    background: GOLD,
                    padding: "1px 5px",
                    borderRadius: "4px",
                  }}
                  title={t("showrunner.pulse.highlightsTooltip")}
                >
                  {c.highlightCount}🔥
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =============================================================================
// PARTICIPANT ACTIVITY WIDGET (Heatmap participantes × buckets)
// =============================================================================

const ParticipantActivityWidget: React.FC<{ activity: ParticipantActivity }> = ({ activity }) => {
  const { t } = useTranslation();
  if (!activity || !activity.participants || activity.participants.length === 0) {
    return <EmptyState icon="bx-time-five" text={t("showrunner.pulse.noActivityData")} />;
  }

  const { buckets, participants, matrix, maxCount } = activity;
  const everyN = Math.max(1, Math.floor(buckets.length / 8));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", height: "100%" }}>
      <div style={{ overflowX: "auto" }} className="thin-scrollbar">
        <table
          style={{
            borderCollapse: "separate",
            borderSpacing: "1px",
            width: "100%",
            tableLayout: "fixed",
          }}
        >
          <tbody>
            {participants.map((p, rowIdx) => (
              <tr key={p.id}>
                <td
                  style={{
                    padding: "0 6px 0 0",
                    width: "120px",
                    maxWidth: "120px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        borderRadius: "50%",
                        background: p.image
                          ? `url(${p.image}) center/cover`
                          : "rgba(255,255,255,0.1)",
                        border: `1px solid ${rowIdx < 3 ? GOLD : "rgba(255,255,255,0.1)"}`,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.55rem",
                        fontWeight: 700,
                        color: GOLD,
                      }}
                    >
                      {!p.image && (p.alias?.[0] || p.name?.[0] || "?")}
                    </div>
                    <span
                      style={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: rowIdx < 3 ? GOLD : "#ddd",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={p.name}
                    >
                      {p.alias || p.name}
                    </span>
                    <span
                      style={{
                        fontSize: "0.55rem",
                        color: GOLD_DIM,
                        marginLeft: "auto",
                        flexShrink: 0,
                      }}
                    >
                      {p.total}
                    </span>
                  </div>
                </td>
                {matrix[rowIdx].map((count, colIdx) => {
                  const intensity = maxCount > 0 ? count / maxCount : 0;
                  const alpha = count === 0 ? 0.04 : 0.2 + intensity * 0.8;
                  return (
                    <td
                      key={colIdx}
                      title={`${p.alias || p.name} · ${buckets[colIdx].label} · ${count} log${count === 1 ? "" : "s"}`}
                      style={{
                        height: "16px",
                        background:
                          count === 0 ? "rgba(255,255,255,0.03)" : `rgba(245, 158, 11, ${alpha})`,
                        borderRadius: "2px",
                      }}
                    />
                  );
                })}
              </tr>
            ))}
            <tr>
              <td />
              {buckets.map((b, idx) => (
                <td
                  key={b.ts}
                  style={{
                    fontSize: "0.45rem",
                    color: "rgba(255,255,255,0.3)",
                    textAlign: "center",
                    fontFamily: "var(--font-mono, monospace)",
                    paddingTop: "4px",
                  }}
                >
                  {idx % everyN === 0 ? b.label : ""}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =============================================================================
// EMPTY STATE
// =============================================================================

const EmptyState: React.FC<{ icon: string; text: string }> = ({ icon, text }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      minHeight: "120px",
      color: "rgba(255,255,255,0.15)",
      gap: "8px",
    }}
  >
    <i className={`bx ${icon}`} style={{ fontSize: "2rem" }}></i>
    <span style={{ fontSize: "0.7rem", fontWeight: 600, letterSpacing: "1px" }}>{text}</span>
  </div>
);
